import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/FlashcardViewer.css";

function FlashcardViewer() {
  const { databaseId } = useParams();
  const navigate = useNavigate();
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFlashcardData = async () => {
      try {
        // Retrieve selected fields from localStorage
        const fieldsConfig = JSON.parse(
          localStorage.getItem("flashcardFields")
        );
        if (!fieldsConfig) {
          navigate(`/fields/${databaseId}`);
          return;
        }

        // Fetch database items
        const response = await axios.get(
          `/api/database-items.php?databaseId=${databaseId}`
        );

        // Transform items into flashcards format
        const cards = response.data.map((item) => ({
          id: item.id,
          front: item[fieldsConfig.frontField] || "No content",
          back: fieldsConfig.backFields.reduce((acc, fieldName) => {
            if (item[fieldName] !== undefined) {
              acc[fieldName] = item[fieldName];
            }
            return acc;
          }, {}),
        }));

        setFlashcards(cards);
      } catch (err) {
        setError("Failed to fetch flashcard data. Please try again.");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFlashcardData();
  }, [databaseId, navigate]);

  const handleFlip = useCallback(() => {
    setFlipped(!flipped);
  }, [flipped]);

  const handlePrevious = useCallback(() => {
    setFlipped(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? flashcards.length - 1 : prevIndex - 1
    );
  }, [flashcards.length]);

  const handleNext = useCallback(() => {
    setFlipped(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === flashcards.length - 1 ? 0 : prevIndex + 1
    );
  }, [flashcards.length]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePrevious, handleNext]);

  if (loading) return <div className="loading">Loading flashcards...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (flashcards.length === 0)
    return (
      <div className="empty-state">
        No flashcards found. Please check your database.
      </div>
    );

  const currentCard = flashcards[currentIndex];

  return (
    <div className="flashcard-viewer">
      <div className="progress-info">
        Card {currentIndex + 1} of {flashcards.length}
      </div>

      <div
        className={`flashcard ${flipped ? "flipped" : ""}`}
        onClick={handleFlip}
      >
        <div className="flashcard-inner">
          <div className="flashcard-front">
            <div className="content">{currentCard.front}</div>
            <div className="instructions">Click to flip</div>
          </div>
          <div className="flashcard-back">
            <div className="content">
              {Object.entries(currentCard.back).map(([field, value]) => (
                <div key={field} className="back-field">
                  <span className="field-name">{field}:</span>
                  <span className="field-value">
                    {Array.isArray(value) ? value.join(", ") : value}
                  </span>
                </div>
              ))}
            </div>
            <div className="instructions">Click to flip</div>
          </div>
        </div>
      </div>

      <div className="navigation-controls">
        <button className="nav-button" onClick={handlePrevious}>
          &larr; Previous
        </button>
        <button className="nav-button" onClick={handleNext}>
          Next &rarr;
        </button>
      </div>

      <button
        className="back-button"
        onClick={() => navigate(`/fields/${databaseId}`)}
      >
        Change Fields
      </button>
    </div>
  );
}

export default FlashcardViewer;
